/**
 * Categories — a name and one of the ten mark colours, by name — drawn the
 * same way wherever Activity has them: the Inbox's (`docs/activity.md`
 * §7.10, each person's own) and the tasks' (§8.7, the company's, Aaron,
 * Oct 25, note 1.5: "let's use the same thing … Task categories and email
 * categories are different").
 *
 * One set of furniture — the chip, the swatches, **New category** /
 * **Edit category**, **Edit categories…** and the filter chip — handed a
 * `CategorySet` that says where its categories live and the words it uses,
 * so the two can never drift apart in look and never share a single row.
 * Colours are `markInk`/`markTint` over `MARK_PALETTE`, so none is typed here.
 */
import { useState, type ReactNode } from 'react'
import { MARK_COLOUR_NAMES, type MarkColourName } from '@rowted/shared'
import { Popover, ConfirmDialog } from '../components/overlays.js'
import { SheetOrDialog } from '../components/sheet-or-dialog.js'
import { Button } from '../components/primitives.js'
import { Field } from '../components/fields.js'
import { markInk, markTint } from '../frame/mark-glyphs.js'
import { cn } from '../lib/cn.js'
import { usePhone } from '../lib/use-narrow.js'
import { activityRequest } from './api.js'
import { Glyph } from './glyphs.js'
import { BackLayer } from './use-activity-history.js'

/** A category, whichever set it is from — the Inbox's and the tasks' have the same shape. */
export interface Category {
  id: string
  name: string
  colour: MarkColourName
}

/** Where a set of categories lives and what it says about itself. */
export interface CategorySet {
  /** `/conversations/categories` or `/activity/task-categories`: GET, POST, PATCH `/:id`, POST `/:id/remove`. */
  endpoint: string
  /** Under **New category** / **Edit category**. */
  dialogSub: string
  /** The **Edit categories…** dialog's title and the line under it. */
  editorTitle: string
  editorSub: string
  /** The name box's example. */
  placeholder: string
  /** A removal that reaches other people asks first; the sentence it asks with. */
  removeConfirm?: (category: Category) => string
  /**
   * The layers the editor and the dialog open on — 60 and 70 over a page;
   * higher when they open over a sheet that is itself on 60 or 70.
   */
  layers?: { editor: number; dialog: number }
}

const message = (cause: unknown): string => (cause instanceof Error ? cause.message : String(cause)) || 'That didn’t save. Try again.'

/** A category on a row or in a filter: its dot and its name, in its tint. */
export function CategoryChip({
  category, size = 'row',
}: { category: Pick<Category, 'name' | 'colour'>; size?: 'row' | 'small' }) {
  return (
    <span data-category={category.name}
      className={cn('inline-flex max-w-[140px] shrink-0 items-center gap-1 whitespace-nowrap rounded-md font-semibold',
        size === 'row' ? 'h-[22px] px-[7px] text-[11px]' : 'h-[18px] px-1.5 text-[10.5px]')}
      style={{ background: markTint(category.colour), color: markInk(category.colour) }}>
      <i aria-hidden className={cn('block shrink-0 rounded-full', size === 'row' ? 'h-[7px] w-[7px]' : 'h-1.5 w-1.5')}
        style={{ background: markInk(category.colour) }} />
      <span className="truncate">{category.name}</span>
    </span>
  )
}

/** The ten colours as a row of swatches, one of them picked. */
export function Swatches({ value, onChange }: { value: MarkColourName; onChange: (colour: MarkColourName) => void }) {
  return (
    <div role="radiogroup" aria-label="Colour" className="flex flex-wrap gap-2">
      {MARK_COLOUR_NAMES.map((colour) => (
        <button key={colour} type="button" role="radio" aria-checked={colour === value} aria-label={colour}
          onClick={() => onChange(colour)}
          className={cn('h-7 w-7 rounded-full border-2', colour === value ? 'border-text' : 'border-transparent')}
          style={{ background: markInk(colour), boxShadow: colour === value ? 'inset 0 0 0 2px rgb(var(--color-surface))' : undefined }} />
      ))}
    </div>
  )
}

/** **New category** and **Edit category**: a name and a colour. */
export function CategoryDialog({
  set, category, onClose, onSaved,
}: {
  set: CategorySet
  category: Category | null
  onClose: () => void
  onSaved: (category: Category) => void
}) {
  const [name, setName] = useState(category?.name ?? '')
  const [colour, setColour] = useState<MarkColourName>(category?.colour ?? 'blue')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /* A dialog pads its own body; a sheet's rows run edge to edge, so a form in one pads itself. */
  const phone = usePhone()
  const save = async () => {
    setBusy(true)
    try {
      const saved = await activityRequest<Category>(
        category ? `${set.endpoint}/${category.id}` : set.endpoint,
        { method: category ? 'PATCH' : 'POST', body: { name, colour } })
      onSaved(saved)
      onClose()
    } catch (cause) {
      setError(message(cause))
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
    {/* On a phone it is a sheet, and back closes it first (Aaron, Oct 26). */}
    {phone && <BackLayer onBack={onClose} />}
    <SheetOrDialog title={category ? 'Edit category' : 'New category'} sub={set.dialogSub}
      width={420} layer={set.layers?.dialog ?? 70} onClose={onClose}
      footer={(
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={busy || !name.trim()} onClick={() => void save()}>
            {category ? 'Save' : 'Make it'}
          </Button>
        </>
      )}>
      <form className={cn('flex flex-col gap-3.5', phone && 'p-4')} onSubmit={(event) => { event.preventDefault(); if (name.trim()) void save() }}>
        <Field label="Name" value={name} onChange={setName} autoFocus placeholder={set.placeholder} />
        <div className="flex flex-col gap-1.5">
          <span className="text-[12px] font-semibold text-text-muted">Colour</span>
          <Swatches value={colour} onChange={setColour} />
        </div>
        <div className="flex items-center gap-2 text-[12px] text-text-muted">
          Looks like <CategoryChip category={{ name: name.trim() || 'Category', colour }} />
        </div>
        {error && <div className="text-[12px] text-danger">{error}</div>}
      </form>
    </SheetOrDialog>
    </>
  )
}

/** **Edit categories…**: each one, to rename, recolour or remove. */
export function CategoriesEditor({
  set, categories, onClose, onChanged,
}: { set: CategorySet; categories: Category[]; onClose: () => void; onChanged: () => void }) {
  const [editing, setEditing] = useState<Category | 'new' | null>(null)
  const [asking, setAsking] = useState<Category | null>(null)
  const [error, setError] = useState<string | null>(null)
  const phone = usePhone()
  const remove = async (category: Category) => {
    try {
      await activityRequest(`${set.endpoint}/${category.id}/remove`, { method: 'POST', body: {} })
      onChanged()
    } catch (cause) {
      setError(message(cause))
    }
  }
  return (
    <>
      {phone && <BackLayer onBack={onClose} />}
      <SheetOrDialog title={set.editorTitle} sub={set.editorSub} width={460}
        layer={set.layers?.editor ?? 60} onClose={onClose}
        footer={(
          <>
            <Button onClick={() => setEditing('new')}><Glyph name="plus" size={14} />New category</Button>
            <Button variant="primary" onClick={onClose}>Done</Button>
          </>
        )}>
        <div className={cn('flex flex-col', phone ? 'py-1' : '-my-2')}>
          {categories.length === 0 && (
            <div className="px-4 py-6 text-center text-[13px] text-text-muted">No categories yet.</div>
          )}
          {categories.map((category) => (
            <div key={category.id} className={cn('flex h-12 items-center gap-2 border-b border-line-faint last:border-b-0',
              phone && 'px-4')}>
              <CategoryChip category={category} />
              <span className="flex-1" />
              <Button variant="ghost" onClick={() => setEditing(category)}>Edit</Button>
              <Button variant="danger" onClick={() => (set.removeConfirm ? setAsking(category) : void remove(category))}>
                Remove
              </Button>
            </div>
          ))}
          {error && <div className="px-4 py-2 text-[12px] text-danger">{error}</div>}
        </div>
      </SheetOrDialog>
      {editing && (
        <CategoryDialog set={set} category={editing === 'new' ? null : editing} onClose={() => setEditing(null)}
          onSaved={() => onChanged()} />
      )}
      {asking && set.removeConfirm && (
        <ConfirmDialog title={`Remove “${asking.name}”?`} body={set.removeConfirm(asking)} confirmLabel="Remove"
          layer={set.layers?.dialog ?? 70} onCancel={() => setAsking(null)}
          onConfirm={() => { const gone = asking; setAsking(null); void remove(gone) }} />
      )}
    </>
  )
}

/** The toolbar's category filter: the categories, one of them narrowing the list (§7.10). */
export function CategoryFilter({
  categories, value, onChange, onEdit, height = 30,
}: {
  categories: Category[]
  value: string | null
  onChange: (categoryId: string | null) => void
  onEdit: () => void
  height?: 30 | 32
}) {
  const [at, setAt] = useState<HTMLElement | null>(null)
  const picked = categories.find((category) => category.id === value) ?? null
  if (categories.length === 0 && !picked) return null
  return (
    <>
      <button type="button" aria-pressed={picked !== null} aria-haspopup="menu"
        onClick={(event) => setAt(at ? null : event.currentTarget)}
        className={cn('inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-[12.5px] font-semibold',
          picked ? 'border-accent-line bg-accent-soft text-accent-dark' : 'border-line bg-surface text-text hover:bg-hover')}
        style={{ height }}>
        {picked
          ? <i aria-hidden className="block h-2 w-2 rounded-full" style={{ background: markInk(picked.colour) }} />
          : <Glyph name="tag" size={13} />}
        {picked ? picked.name : 'Category'}
        <Glyph name="chevronDown" size={12} />
      </button>
      {at && (
        <Popover open anchor={at} onClose={() => setAt(null)} width={240} label="Category" className="py-1.5">
          <CategoryMenuRow on={picked === null} onClick={() => { onChange(null); setAt(null) }}>Every category</CategoryMenuRow>
          {categories.map((category) => (
            <CategoryMenuRow key={category.id} on={category.id === value} swatch={category.colour}
              onClick={() => { onChange(category.id); setAt(null) }}>{category.name}</CategoryMenuRow>
          ))}
          <div className="my-1 border-t border-line-soft" />
          <CategoryMenuRow on={false} onClick={() => { setAt(null); onEdit() }}>Edit categories…</CategoryMenuRow>
        </Popover>
      )}
    </>
  )
}

/** One row of a category menu: a tick when it is on, its dot, its name. */
export function CategoryMenuRow({ on, swatch, onClick, children, role = 'menuitemradio' }: {
  on: boolean; swatch?: MarkColourName; onClick: () => void; children: ReactNode
  role?: 'menuitemradio' | 'menuitemcheckbox'
}) {
  return (
    <button type="button" role={role} aria-checked={on} onClick={onClick}
      className="flex h-8 w-full items-center gap-2 px-3 text-left text-[13px] hover:bg-hover">
      <span aria-hidden className="w-3.5 shrink-0 text-accent">{on ? '✓' : ''}</span>
      {swatch && <i aria-hidden className="block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: markInk(swatch) }} />}
      <span className="truncate">{children}</span>
    </button>
  )
}
